import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { currentSubscription, disablePush, enablePush, pushSupported } from '../lib/push';
import { ALERT_GROUPS, alertLabel } from '../lib/alertLabels';
import { Button, Card, cx, ErrorText, Pill } from './ui';

/** Settings → Notifications (INT-ALR-03): this device's browser notifications and per-alert channels. */

type Channel = 'IN_APP' | 'WEB_PUSH' | 'TELEGRAM' | 'EMAIL';
interface EventPref {
  key: string;
  critical: boolean;
  digestable: boolean;
  defaultChannels: Channel[];
  channels: Channel[];
  custom: boolean;
  digest: boolean;
}
interface Prefs {
  reach: { email: string; emailConfigured: boolean; telegramLinked: boolean; telegramConfigured: boolean; pushDevices: number; pushConfigured: boolean };
  events: EventPref[];
}

const COLS: { ch: Channel; label: string }[] = [
  { ch: 'WEB_PUSH', label: 'Browser' },
  { ch: 'TELEGRAM', label: 'Telegram' },
  { ch: 'EMAIL', label: 'Email' },
];

export function NotificationSettings() {
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: ['notification-prefs'], queryFn: () => api<Prefs>('/notifications/preferences') });
  const [device, setDevice] = useState<'unknown' | 'on' | 'off' | 'unsupported'>('unknown');
  const [deviceMsg, setDeviceMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!pushSupported()) return setDevice('unsupported');
    void currentSubscription().then((s) => setDevice(s ? 'on' : 'off'));
  }, []);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['notification-prefs'] });

  const toggleDevice = useMutation({
    mutationFn: async () => {
      if (device === 'on') {
        await disablePush();
        return 'off' as const;
      }
      return enablePush();
    },
    onSuccess: (r) => {
      setDeviceMsg(r === 'denied' ? 'Notifications are blocked for this site in your browser settings. Allow them there, then try again.' : r === 'unavailable' ? 'Browser notifications aren’t set up on this server yet.' : null);
      setDevice(r === 'on' ? 'on' : 'off');
      refresh();
    },
  });
  // Ticks live in local state so they change on the same click; the server's normalised answer (e.g. a critical
  // alert keeping a push channel) then replaces the row, and a failed save reloads from the server.
  const [events, setEvents] = useState<EventPref[]>([]);
  useEffect(() => {
    if (prefs.data) setEvents(prefs.data.events);
  }, [prefs.data]);
  const save = useMutation({
    mutationFn: (p: { key: string; channels: Channel[]; digest: boolean }) => api<{ key: string; channels: Channel[]; digest: boolean }>(`/notifications/preferences/${p.key}`, { method: 'PUT', body: { channels: p.channels, digest: p.digest } }),
    onSuccess: (r) => setEvents((list) => list.map((e) => (e.key === r.key ? { ...e, channels: r.channels, digest: r.digest, custom: true } : e))),
    onError: refresh,
  });
  const apply = (p: { key: string; channels: Channel[]; digest: boolean }) => {
    setEvents((list) => list.map((e) => (e.key === p.key ? { ...e, channels: ['IN_APP', ...p.channels.filter((c) => c !== 'IN_APP')], digest: p.digest, custom: true } : e)));
    save.mutate(p);
  };
  const reset = useMutation({ mutationFn: (key: string) => api(`/notifications/preferences/${key}`, { method: 'DELETE' }), onSuccess: refresh });

  const grouped = useMemo(() => {
    const out = new Map<string, (EventPref & { label: string })[]>();
    for (const e of events) {
      const { label, group } = alertLabel(e.key);
      out.set(group, [...(out.get(group) ?? []), { ...e, label }]);
    }
    return ALERT_GROUPS.filter((g) => out.has(g)).map((g) => [g, out.get(g)!] as const);
  }, [events]);
  const reach = prefs.data?.reach;

  const toggle = (e: EventPref, ch: Channel) => {
    const has = e.channels.includes(ch);
    apply({ key: e.key, channels: has ? e.channels.filter((c) => c !== ch) : [...e.channels, ch], digest: e.digest });
  };

  return (
    <Card className="flex flex-col gap-4 p-4 lg:col-span-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 text-[15px] font-semibold">Notifications</h2>
          <p className="m-0 text-sm text-sub">Choose where each alert reaches you. In-app is always on. Critical alerts always reach you in-app plus Browser or Telegram, even in quiet hours.</p>
        </div>
      </div>

      <div className="grid gap-2.5 md:grid-cols-3">
        <div className="flex flex-col gap-2 rounded-lg bg-raised p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Browser on this device</span>
            <Pill tone={device === 'on' ? 'up' : 'neutral'}>{device === 'on' ? 'On' : device === 'unsupported' ? 'Not supported' : 'Off'}</Pill>
          </div>
          <span className="text-xs text-muted">{reach ? `${reach.pushDevices} device${reach.pushDevices === 1 ? '' : 's'} switched on` : ''}{reach && !reach.pushConfigured ? ' · not set up on this server yet' : ''}</span>
          {device !== 'unsupported' && (
            <Button className="self-start" onClick={() => toggleDevice.mutate()} disabled={toggleDevice.isPending || device === 'unknown'}>
              {device === 'on' ? 'Turn off on this device' : 'Turn on for this device'}
            </Button>
          )}
          {device === 'unsupported' && <span className="text-xs text-sub">On iPhone, add VTF to your Home Screen first (Share → Add to Home Screen), then open it from there.</span>}
          {deviceMsg && <span className="text-xs text-warn">{deviceMsg}</span>}
          <ErrorText error={toggleDevice.error ? errorText(toggleDevice.error) : null} />
        </div>
        <div className="flex flex-col gap-1 rounded-lg bg-raised p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Telegram</span>
            <Pill tone={reach?.telegramLinked ? 'up' : 'neutral'}>{reach?.telegramLinked ? 'Linked' : 'Not linked'}</Pill>
          </div>
          <span className="text-xs text-muted">{reach?.telegramConfigured === false ? 'The bot isn’t set up on this server yet.' : 'Link it in the Telegram card above.'}</span>
        </div>
        <div className="flex flex-col gap-1 rounded-lg bg-raised p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Email</span>
            <Pill tone={reach?.emailConfigured ? 'up' : 'neutral'}>{reach?.emailConfigured ? 'Ready' : 'Not set up'}</Pill>
          </div>
          <span className="truncate text-xs text-muted">{reach?.email}{reach && !reach.emailConfigured ? ' · the server has no email account yet' : ''}</span>
        </div>
      </div>

      <ErrorText error={prefs.error ? errorText(prefs.error) : save.error ? errorText(save.error) : null} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="py-2 pr-3 font-medium">Alert</th>
              {COLS.map((c) => <th key={c.ch} className="w-24 px-2 py-2 text-center font-medium">{c.label}</th>)}
              <th className="w-28 px-2 py-2 text-center font-medium">Digest</th>
              <th className="w-20 py-2" />
            </tr>
          </thead>
          {grouped.map(([group, events]) => (
            <tbody key={group}>
              <tr>
                <th colSpan={6} className="pt-4 pb-1 text-left text-xs font-semibold tracking-[0.06em] text-muted uppercase">{group}</th>
              </tr>
              {events.map((e) => (
                <tr key={e.key} className="border-t border-[#1a1f27]">
                  <td className="py-2 pr-3">
                    <span className="text-ink">{e.label}</span>
                    {e.critical && <Pill tone="warn" className="ml-2">critical</Pill>}
                  </td>
                  {COLS.map((c) => (
                    <td key={c.ch} className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        className="h-[18px] w-[18px]"
                        aria-label={`${e.label} by ${c.label}`}
                        checked={e.channels.includes(c.ch)}
                        onChange={() => toggle(e, c.ch)}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-2 text-center">
                    {e.digestable ? (
                      <input type="checkbox" className="h-[18px] w-[18px]" aria-label={`${e.label}: bundle into a digest`} checked={e.digest} onChange={() => apply({ key: e.key, channels: e.channels, digest: !e.digest })} />
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                  <td className={cx('py-2 text-right')}>
                    {e.custom && <button className="text-xs text-accent" onClick={() => reset.mutate(e.key)}>Default</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      <p className="m-0 text-xs text-muted">Digest bundles Browser, Telegram and Email for that alert into one message at 08:00, 13:00 and 18:00 your time. In-app still shows each one as it happens.</p>
    </Card>
  );
}
